/* =============================================================================
 * Leo Hobby — Cart continuity across market domains
 * -----------------------------------------------------------------------------
 * The store runs one Shopify Markets catalogue across several country domains
 * (leohobby.com, .si, .it, .de, .fr, .es, .pt, .eu). A Shopify cart is bound to
 * the domain it was created on, so moving from one domain to another shows an
 * empty cart. The shopper reads this as "my cart was reset".
 *
 * This module carries the cart from the old domain to the new one with NO
 * external service and NO cost:
 *   - OUTBOUND: when the shopper follows a link to a sibling Leo Hobby domain,
 *     the current cart (items + line item properties, note, attributes) is
 *     encoded into a `?cartsync=` URL parameter. If a discount code is active,
 *     the link is routed through the target domain's `/discount/<code>` endpoint
 *     so the code is re-applied natively.
 *   - INBOUND: on arrival, if `?cartsync=` is present and the local cart is
 *     empty, the cart is rebuilt with the Shopify AJAX API (`/cart/add.js`),
 *     then the page reloads to the clean URL so the theme renders the populated
 *     cart, drawer and header count natively.
 *
 * The URL is the storage: there is no backend, no account and nothing to host.
 * Variant IDs are identical across the domains of one Markets store, so no ID
 * mapping is needed (verified live: White (100) = 58080303284572 on .com and .si).
 *
 * What this does NOT do: a silent geo auto-redirect performed by Shopify itself
 * (no click, no link) cannot be intercepted from theme code — that case is
 * covered by geolocation-on-entry (shopper lands on the right domain before
 * adding anything) and by enabling Shopify Markets "recommendation" mode.
 *
 * Safe by design: every step is wrapped in try/catch and never blocks the page;
 * if anything fails the navigation proceeds normally without the cart param.
 * Does not touch the shipping bar, terms gate or swatch picker.
 * ========================================================================== */
(function () {
  'use strict';

  var BRAND = (window.LH_CART_SYNC_BRAND || 'leohobby').toLowerCase();
  var PARAM = 'cartsync';
  var MAX_ITEMS = 60;        // guard against pathological URLs
  var MAX_QTY = 999;

  // Most recent cart snapshot, kept fresh from the theme's own cart events.
  var cachedCart = null;
  var lastFetch = 0; // throttle prewarm fetches

  /* --------------------------------------------------------------------------
   * Helpers
   * ------------------------------------------------------------------------ */

  function normHost(host) {
    return String(host || '').replace(/^www\./, '').toLowerCase();
  }

  // A sibling storefront is a bare two-label brand domain (leohobby.<tld>) that
  // is not the current host. Subdomains like account./store. are NOT siblings.
  function isSiblingHost(host) {
    var n = normHost(host);
    var parts = n.split('.');
    return parts.length === 2 && parts[0] === BRAND && n !== normHost(location.hostname);
  }

  function readCookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function getCart(cb) {
    try {
      fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { cachedCart = c; cb(c); })
        .catch(function () { cb(null); });
    } catch (e) { cb(null); }
  }

  /* --------------------------------------------------------------------------
   * OUTBOUND — encode the current cart into a transfer URL
   * ------------------------------------------------------------------------ */

  function encodeCart(cart) {
    if (!cart || !cart.items || !cart.items.length) return '';
    var items = [];
    for (var i = 0; i < cart.items.length && items.length < MAX_ITEMS; i++) {
      var it = cart.items[i];
      var o = { id: it.id, q: it.quantity };
      if (it.properties && Object.keys(it.properties).length) o.p = it.properties;
      if (it.selling_plan_allocation && it.selling_plan_allocation.selling_plan) {
        o.s = it.selling_plan_allocation.selling_plan.id;
      }
      items.push(o);
    }
    if (!items.length) return '';
    var snap = { v: 1, i: items };
    if (cart.note) snap.n = cart.note;
    if (cart.attributes && Object.keys(cart.attributes).length) snap.a = cart.attributes;
    try { return encodeURIComponent(JSON.stringify(snap)); } catch (e) { return ''; }
  }

  // Build the final URL for a sibling-domain destination, carrying the cart and
  // (if present) the active discount code. Returns null when there is nothing
  // to carry or the destination is not a sibling domain.
  // The cart param is appended manually (single percent-encoding) and read back
  // the same way in tryRestore(), so the encoding is symmetric and the URL stays
  // as short as possible.
  function buildTransferUrl(rawHref) {
    try {
      var u = new URL(rawHref, location.href);
      if (!isSiblingHost(u.hostname)) return null;

      var encoded = encodeCart(cachedCart);
      if (!encoded) return null; // empty cart -> nothing to transfer, leave link as-is

      var sep = u.search ? '&' : '?';
      var pathAndQuery = u.pathname + u.search + sep + PARAM + '=' + encoded + u.hash;

      var code = readCookie('discount_code');
      if (code) {
        return u.protocol + '//' + u.host + '/discount/' + encodeURIComponent(code) +
          '?redirect=' + encodeURIComponent(pathAndQuery);
      }
      return u.protocol + '//' + u.host + pathAndQuery;
    } catch (e) { return null; }
  }

  function onSiblingLinkInteraction(evt) {
    // Pre-warm the cart snapshot when a sibling link is about to be used, so the
    // synchronous click handler has the freshest possible cart. Throttled, and
    // only for the (rare) links that actually point to another brand domain.
    var a = evt.target && evt.target.closest && evt.target.closest('a[href]');
    if (!a) return;
    var host;
    try { host = new URL(a.href, location.href).hostname; } catch (e) { return; }
    if (!isSiblingHost(host)) return;
    var now = (Date.now ? Date.now() : new Date().getTime());
    if (now - lastFetch < 2000) return;
    lastFetch = now;
    getCart(function () {});
  }

  function onClickCapture(evt) {
    try {
      var a = evt.target && evt.target.closest && evt.target.closest('a[href]');
      if (!a) return;

      var dest;
      try { dest = new URL(a.href, location.href); } catch (e) { return; }
      if (!isSiblingHost(dest.hostname)) return;

      var modified = evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.altKey ||
        (typeof evt.button === 'number' && evt.button !== 0);

      var transfer = buildTransferUrl(a.href);
      if (!transfer) return; // empty cart or build failed -> leave native behaviour

      if (modified) {
        // New tab / window: rewrite the href so the native action carries the cart.
        a.href = transfer;
        return;
      }
      // Take over the navigation before any app/Markets handler can redirect.
      evt.preventDefault();
      evt.stopImmediatePropagation();
      window.location.assign(transfer);
    } catch (e) { /* never block a click */ }
  }

  // The theme's localization form posts to /localization (same domain). If a
  // country change makes Shopify redirect to another market domain, carry the
  // cart through the form's return_to value.

  // True for the native Shopify localization form — the one BOTH the theme's own
  // country/language selector and the Selecty geo popup submit. Matched by class
  // or /localization action so a markup tweak in either can't slip past, and so
  // no other form in the store is ever touched.
  function isLocalizationForm(form) {
    if (!form) return false;
    if ((form.className || '').indexOf('shopify-localization-form') !== -1) return true;
    var action = '';
    try { action = form.getAttribute('action') || ''; } catch (e) {}
    return action.indexOf('/localization') !== -1;
  }

  // Append the cart snapshot to a localization form's return_to so it survives a
  // cross-market redirect. Idempotent: it only stamps when cartsync is absent, so
  // calling it from the listeners and from the submit() wrapper never doubles up.
  function applyCartsyncToForm(form) {
    if (!isLocalizationForm(form)) return;
    var encoded = encodeCart(cachedCart);
    if (!encoded) return;
    var rt = form.querySelector('input[name="return_to"]');
    if (!rt) return;
    var base = rt.value || (location.pathname + location.search);
    var sep = base.indexOf('?') === -1 ? '?' : '&';
    if (base.indexOf(PARAM + '=') === -1) rt.value = base + sep + PARAM + '=' + encoded;
  }

  function onLocalizationSubmit(evt) {
    try { applyCartsyncToForm(evt.target); } catch (e) { /* ignore */ }
  }

  // The theme's country/language <select> uses onchange="this.form.submit()",
  // and form.submit() fires NO submit event. A capture-phase change listener
  // runs before that inline handler, so return_to is stamped in time.
  function onLocalizationChange(evt) {
    try {
      var el = evt.target;
      if (!el) return;
      applyCartsyncToForm(el.form || (el.closest && el.closest('form')));
    } catch (e) { /* ignore */ }
  }

  /* --------------------------------------------------------------------------
   * INBOUND — rebuild the cart from the transfer URL
   * ------------------------------------------------------------------------ */

  // Parse the snapshot tolerating one or two layers of percent-encoding. The
  // direct link path produces a single-encoded value; routing through Shopify's
  // /discount/<code>?redirect= endpoint adds a second layer. Try the least
  // decoding that yields valid JSON first, so encoded payloads never corrupt.
  function parseSnapAny(raw) {
    var candidates = [raw];
    try { candidates.push(decodeURIComponent(raw)); } catch (e) {}
    try { candidates.push(decodeURIComponent(decodeURIComponent(raw))); } catch (e) {}
    for (var i = 0; i < candidates.length; i++) {
      var s = candidates[i];
      if (!s || s.charAt(0) !== '{') continue; // still percent-encoded -> skip
      try {
        var obj = JSON.parse(s);
        if (obj && obj.v === 1 && Array.isArray(obj.i)) return obj;
      } catch (e) {}
    }
    return null;
  }

  function decodeSnapshot(raw) {
    try {
      var snap = parseSnapAny(raw);
      if (!snap) return null;
      var items = [];
      for (var k = 0; k < snap.i.length && items.length < MAX_ITEMS; k++) {
        var o = snap.i[k] || {};
        var id = parseInt(o.id, 10);
        var q = parseInt(o.q, 10);
        if (!id || id < 1 || !q || q < 1 || q > MAX_QTY) continue;
        var li = { id: id, quantity: q };
        if (o.p && typeof o.p === 'object') li.properties = o.p;
        if (o.s) { var sp = parseInt(o.s, 10); if (sp) li.selling_plan = sp; }
        items.push(li);
      }
      if (!items.length) return null;
      return {
        items: items,
        note: typeof snap.n === 'string' ? snap.n : null,
        attributes: (snap.a && typeof snap.a === 'object') ? snap.a : null
      };
    } catch (e) { return null; }
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
  }

  // /cart/add.js with items[] is not atomic: if one variant is unavailable the
  // whole call fails. Fall back to adding items one by one, skipping failures.
  function addItems(items) {
    return postJSON('/cart/add.js', { items: items }).then(function (res) {
      if (res.ok) return true;
      var chain = Promise.resolve();
      items.forEach(function (li) {
        chain = chain.then(function () {
          return postJSON('/cart/add.js', { items: [li] }).catch(function () {});
        });
      });
      return chain.then(function () { return true; });
    });
  }

  function cleanUrlString() {
    try {
      var u = new URL(location.href);
      u.searchParams.delete(PARAM);
      return u.pathname + u.search + u.hash;
    } catch (e) { return location.pathname; }
  }

  function stripParamNoReload() {
    try { history.replaceState({}, document.title, cleanUrlString()); } catch (e) {}
  }

  function tryRestore() {
    // Read the raw (single percent-encoded) value straight from the query string,
    // matching how buildTransferUrl wrote it. decodeSnapshot does the one decode.
    var raw;
    try {
      var m = location.search.match(new RegExp('[?&]' + PARAM + '=([^&]*)'));
      raw = m ? m[1] : null;
    } catch (e) { return; }
    if (!raw) return;

    // Guard against re-running on the same payload (e.g. back/forward).
    var guardKey = 'lh_cartsync_done';
    try {
      if (sessionStorage.getItem(guardKey) === raw) { stripParamNoReload(); return; }
    } catch (e) {}

    var snap = decodeSnapshot(raw);
    if (!snap) { stripParamNoReload(); return; }

    getCart(function (cart) {
      try { sessionStorage.setItem(guardKey, raw); } catch (e) {}

      // Do not clobber a cart the shopper already started on this domain.
      if (cart && cart.item_count > 0) { stripParamNoReload(); return; }

      addItems(snap.items)
        .then(function () {
          if (snap.note || snap.attributes) {
            var upd = {};
            if (snap.note) upd.note = snap.note;
            if (snap.attributes) upd.attributes = snap.attributes;
            return postJSON('/cart/update.js', upd).catch(function () {});
          }
        })
        .then(function () {
          // Reload to the clean URL so the theme renders the populated cart,
          // drawer and header count from the server. One reload, only on a
          // (rare) cross-domain transfer.
          window.location.replace(cleanUrlString());
        })
        .catch(function () { stripParamNoReload(); });
    });
  }

  /* --------------------------------------------------------------------------
   * ACCOUNT SYNC — logged-in cart across domains (incl. cold navigation)
   * --------------------------------------------------------------------------
   * The URL/click path above carries the cart when the shopper switches THROUGH
   * the site (Selecty, the country switcher, a sibling-domain link). It cannot
   * cover a COLD switch — typing the other domain's address or a bookmark — because
   * the browser isolates state per origin and there is no in-site event to hook.
   *
   * For a LOGGED-IN shopper we close that gap with a tiny KV Worker (NO Shopify
   * app): every cart change mirrors the cart (variant id + quantity ONLY) to the
   * Worker keyed by customer id; on any domain where the customer is recognised
   * (natively, or after the snippet's ?sso=silent handshake) we read it back and
   * rebuild an empty cart. Identity is window.LH_CUSTOMER_ID (server-rendered),
   * with a ShopifyAnalytics fallback for the post-sso render race where the cookie
   * is set but the page still rendered as guest. No-op for guests. See
   * docs/logged-in-cartsync-architecture.md.
   * ------------------------------------------------------------------------ */

  // Cross-origin KV Worker endpoint (override with window.LH_CARTSYNC_ENDPOINT).
  var ACCT_ENDPOINT = window.LH_CARTSYNC_ENDPOINT || 'https://leohobby-cartsync.site31123123.workers.dev/s';
  var acctLastWrite = 0;
  var acctLastPayload = null;
  var acctWriteTimer = null;

  // The customer id to key the snapshot on. Prefer the server-rendered value;
  // fall back to ShopifyAnalytics, which reflects login even when the document
  // server-rendered as guest right after a silent SSO handshake.
  function currentCustomerId() {
    if (window.LH_CUSTOMER_ID) return String(window.LH_CUSTOMER_ID);
    try {
      var c = window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page && ShopifyAnalytics.meta.page.customerId;
      if (c) return String(c);
    } catch (e) {}
    return '';
  }
  function isLoggedIn() { return !!currentCustomerId(); }

  // Snapshot for the account store: variant id + quantity ONLY (no properties,
  // note or attributes — nothing personal leaves the storefront). Empty cart ->
  // a clear payload so emptying on one domain can't resurrect it on another.
  function accountSnapshotJSON(cart) {
    if (!cart || !cart.items || !cart.items.length) return '{"v":1,"i":[]}';
    var items = [];
    for (var i = 0; i < cart.items.length && items.length < MAX_ITEMS; i++) {
      var it = cart.items[i];
      if (it && it.id && it.quantity) items.push({ id: it.id, q: it.quantity });
    }
    return JSON.stringify({ v: 1, i: items });
  }

  // Cross-origin POST; no cookies needed (cid carries identity). `keepalive`
  // lets the write outlive the very navigation/unload that triggers the cross-
  // domain restore — without it the LAST add-to-cart before leaving is lost and
  // the other domain shows a stale or empty cart.
  function sendSnapshot(cid, payload, keepalive) {
    try {
      fetch(ACCT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cid: cid, snap: payload }),
        keepalive: !!keepalive
      }).catch(function () {});
    } catch (e) {}
  }

  function pushAccountSnapshot() {
    var cid = currentCustomerId();
    if (!cid) return;
    var payload = accountSnapshotJSON(cachedCart);
    if (!payload || payload === acctLastPayload) return; // unchanged -> skip
    var now = (Date.now ? Date.now() : new Date().getTime());
    if (now - acctLastWrite < 1500) {                    // throttle, keep a trailing write
      if (acctWriteTimer) clearTimeout(acctWriteTimer);
      acctWriteTimer = setTimeout(pushAccountSnapshot, 1600);
      return;
    }
    acctLastWrite = now;
    acctLastPayload = payload;
    sendSnapshot(cid, payload, false);
  }

  // Flush the latest cart on tab-hide / unload with keepalive, so a final change
  // (and any change still sitting in the throttle window) is never lost when the
  // shopper immediately leaves for a sibling domain.
  function flushAccountSnapshot() {
    var cid = currentCustomerId();
    if (!cid) return;
    var payload = accountSnapshotJSON(cachedCart);
    if (!payload || payload === acctLastPayload) return;
    acctLastPayload = payload;
    sendSnapshot(cid, payload, true);
  }

  // Read the snapshot from the Worker and rebuild an empty local cart. Polls a
  // few times because after ?sso=silent the customer id can appear ~1-2s late.
  function tryRestoreFromAccount(attempt) {
    attempt = attempt || 0;
    var cid = currentCustomerId();
    if (!cid) {
      if (attempt < 4) setTimeout(function () { tryRestoreFromAccount(attempt + 1); }, 700);
      return;
    }

    // Guard is keyed on the SNAPSHOT, not just the customer: we only suppress a
    // re-restore of the SAME cart (prevents reload loops) — a cart changed on
    // another domain still restores. Set ONLY after we actually act, so an empty
    // store on a first visit never poisons a later navigation in the same tab.
    var guardKey = 'lh_acct_restore_done';

    fetch(ACCT_ENDPOINT + '?cid=' + encodeURIComponent(cid), { method: 'GET' })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        var rawStr = res && res.snap;
        if (!rawStr) return;                         // nothing stored -> retry on a later navigation
        try { if (sessionStorage.getItem(guardKey) === rawStr) return; } catch (e) {} // same cart already handled
        var snap = decodeSnapshot(rawStr);
        if (!snap) return;
        getCart(function (cart) {
          if (cart && cart.item_count > 0) {         // don't clobber a cart started here
            try { sessionStorage.setItem(guardKey, rawStr); } catch (e) {}
            return;
          }
          addItems(snap.items).then(function () {
            // Reload ONLY if items were actually added. Guards a loop where every
            // stored variant is unavailable (addItems resolves but cart stays
            // empty) and sessionStorage is unavailable (private mode).
            getCart(function (cart2) {
              try { sessionStorage.setItem(guardKey, rawStr); } catch (e) {}
              if (cart2 && cart2.item_count > 0) window.location.reload();
            });
          }).catch(function () {});
        });
      })
      .catch(function () {});
  }

  /* --------------------------------------------------------------------------
   * Wire-up
   * ------------------------------------------------------------------------ */

  // Keep the snapshot fresh from the theme's documented cart events, and mirror
  // each change to the account metafield for logged-in shoppers.
  document.addEventListener('on:cart:add', function (e) {
    if (e.detail && e.detail.cart) cachedCart = e.detail.cart;
    pushAccountSnapshot();
  });
  document.addEventListener('on:line-item:change', function (e) {
    if (e.detail && e.detail.cart) { cachedCart = e.detail.cart; pushAccountSnapshot(); }
    else getCart(function () { pushAccountSnapshot(); });
  });

  // Flush the latest snapshot (with keepalive) when the shopper leaves, so the
  // last cart change survives an immediate cross-domain navigation.
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') flushAccountSnapshot();
  });
  window.addEventListener('pagehide', flushAccountSnapshot);

  // Outbound interception (capture phase, before app/Markets handlers).
  document.addEventListener('click', onClickCapture, true);
  document.addEventListener('mouseover', onSiblingLinkInteraction, true);
  document.addEventListener('focusin', onSiblingLinkInteraction, true);
  document.addEventListener('touchstart', onSiblingLinkInteraction, { capture: true, passive: true });
  document.addEventListener('submit', onLocalizationSubmit, true);
  document.addEventListener('change', onLocalizationChange, true);

  // Selecty (the geo popup) and the theme's own country selector both switch
  // market by calling form.submit() PROGRAMMATICALLY on the localization form
  // (theme: localization-form.liquid `evt.currentTarget.submit()`; Selecty:
  // scripted submit of the same `shopify-localization-form`). A scripted submit()
  // fires neither a 'submit' nor a 'change' event, so the listeners above never
  // see it and the cart param is lost. Wrap the prototype method to stamp the
  // cart into return_to right before the real submit runs. Only localization
  // forms are acted on; every other form.submit() in the store passes straight
  // through to the native method. Installed once (guard flag), and it wraps
  // submit — not fetch — so it cannot collide with the Vitals app's fetch patch.
  try {
    var protoSubmit = HTMLFormElement.prototype.submit;
    if (typeof protoSubmit === 'function' && !protoSubmit.__lhCartSync) {
      var wrappedSubmit = function () {
        try { if (isLocalizationForm(this)) applyCartsyncToForm(this); } catch (e) { /* never block a submit */ }
        return protoSubmit.apply(this, arguments);
      };
      wrappedSubmit.__lhCartSync = true;
      HTMLFormElement.prototype.submit = wrappedSubmit;
    }
  } catch (e) { /* leave native submit untouched on any failure */ }

  // Initial state + inbound restore. Mirror an existing (non-empty) cart for
  // logged-in shoppers — never write an empty cart on load, so a pending account
  // restore is not clobbered. URL restore takes precedence over account restore.
  getCart(function (cart) {
    if (isLoggedIn() && cart && cart.item_count > 0) pushAccountSnapshot();
  });
  tryRestore();
  tryRestoreFromAccount();
})();
